import { colorFor, firstName, initials, type StudentDto } from '@hudhuria/shared';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { z } from 'zod';
import { generalAttendance } from '~/api/actions';
import { useRollCallMutation } from '~/api/mutations';
import {
  useActivities,
  useCenterId,
  useGroups,
  useMe,
  useRoster,
  useToday,
  useTodayData,
} from '~/api/queries';
import { ActivityPicker, NewActivityForm } from '~/components/activity';
import { Chip, EmptyState, Overlay, OverlayFooter, OverlayHeader, PrimaryButton } from '~/components/ui';
import { quickActivities, shortName } from '~/lib/activity';
import { apiUrl } from '~/lib/api';
import { useBack, useFinish } from '~/lib/nav';
import { toast } from '~/lib/toast';
import { uuid } from '~/lib/uuid';

export const Route = createFileRoute('/_app/rollcall/$groupId')({
  validateSearch: z.object({
    activity: z.string().optional(),
    step: z.enum(['pick', 'new']).optional(),
    from: z.string().optional(),
  }),
  component: RollCall,
});

/** Tap faces to mark a whole class or session present in one go. */
function RollCall() {
  const { groupId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const back = useBack();
  const finish = useFinish();
  const me = useMe().data;
  const centerId = useCenterId();
  const today = useToday();
  const groups = useGroups();
  const roster = useRoster().data;
  const activities = useActivities().data ?? [];
  const todayData = useTodayData().data;
  const save = useRollCallMutation();

  const group = groups.data?.find((g) => g.id === groupId);
  const activity =
    activities.find((a) => a.id === search.activity) ?? generalAttendance(activities) ?? activities[0];
  const members = useMemo(() => {
    if (!group || !roster) return [];
    const byId = new Map(roster.map((s) => [s.id, s]));
    return group.studentIds.map((id) => byId.get(id)).filter((s): s is StudentDto => !!s);
  }, [group, roster]);

  // Start from whoever is already in for this activity today, so re-saving a roll-call is safe.
  const alreadyIn = useMemo(
    () =>
      new Set(
        (todayData?.checkins ?? []).filter((c) => c.activityId === activity?.id).map((c) => c.studentId),
      ),
    [todayData, activity?.id],
  );
  const [picked, setPicked] = useState<Set<string> | null>(null);
  const selected = picked ?? new Set(members.filter((m) => alreadyIn.has(m.id)).map((m) => m.id));
  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setPicked(next);
  };
  const allIn = selected.size === members.length && members.length > 0;

  const setStep = (step: 'pick' | 'new' | undefined, activityId?: string) =>
    void navigate({
      to: '.',
      search: (s) => ({ ...s, step, activity: activityId ?? s.activity }),
      replace: step === undefined,
    });

  if (!groups.data || !roster) return <Overlay>{null}</Overlay>;
  if (!group) {
    return (
      <Overlay>
        <OverlayHeader back="Groups" onBack={back} />
        <EmptyState>This group isn't available on this device.</EmptyState>
      </Overlay>
    );
  }

  const submit = () => {
    if (!me || !activity) return;
    if (!selected.size) {
      toast('Tap at least one face');
      return;
    }
    save.mutate({
      input: {
        id: uuid(),
        centerId,
        groupId: group.id,
        activityId: activity.id,
        studentIds: [...selected],
        occurredAt: new Date().toISOString(),
      },
      local: {
        total: members.length,
        feeMinor: activity.feeMinor,
        staffId: me.staff.id,
        localDate: today,
        groupName: group.name,
      },
    });
    toast(`${selected.size} checked in · ${shortName(group.name)}`);
    finish(search.from);
  };

  const quick = quickActivities(activities, today, activity?.id);

  return (
    <Overlay>
      <OverlayHeader back="Groups" onBack={back} border pb={14}>
        <div className="mt-[10px] font-display text-[24px] font-bold tracking-[-0.02em]">{group.name}</div>
        <div className="mt-[3px] text-[13px] text-slate-500">
          {group.type} · {members.length} students · tap faces to mark present
        </div>
        <div className="mt-3 flex gap-[7px] scroll-x">
          {quick.map((a) => (
            <Chip key={a.id} on={a.id === activity?.id} onClick={() => setStep(undefined, a.id)}>
              {shortName(a.builtin ? 'Attendance' : a.name)}
            </Chip>
          ))}
          <Chip on={false} onClick={() => setStep('pick')} className="bg-white text-brand">
            All {activities.length} →
          </Chip>
        </div>
      </OverlayHeader>

      <div className="flex-1 p-[18px] scroll-y">
        <div className="grid grid-cols-4 gap-x-2 gap-y-[14px]">
          {members.map((m) => {
            const on = selected.has(m.id);
            const photo = apiUrl(m.photoUrl);
            return (
              <button
                type="button"
                key={m.id}
                onClick={() => toggle(m.id)}
                className="flex cursor-pointer flex-col items-center gap-1.5 border-none bg-transparent p-0"
              >
                <div
                  className="relative flex h-[60px] w-[60px] items-center justify-center rounded-full font-display text-[16px] font-bold transition-colors"
                  style={{ background: on ? colorFor(m.name) : '#eef1f5', color: on ? '#fff' : '#94a3b8' }}
                >
                  {photo ? (
                    <img
                      src={photo}
                      alt=""
                      className="h-full w-full rounded-full object-cover"
                      style={{ opacity: on ? 1 : 0.45, filter: on ? 'none' : 'grayscale(1)' }}
                    />
                  ) : (
                    initials(m.name)
                  )}
                  <div
                    className="absolute -right-0.5 -bottom-0.5 flex h-5 w-5 items-center justify-center rounded-full border-[2.5px] border-white text-[11px] text-white"
                    style={{ background: on ? '#10b981' : '#cbd5e1' }}
                  >
                    {on ? '✓' : ''}
                  </div>
                </div>
                <div
                  className="max-w-[62px] truncate text-center text-[11px] font-medium"
                  style={{ color: on ? '#0f172a' : '#94a3b8' }}
                >
                  {firstName(m.name)}
                </div>
              </button>
            );
          })}
        </div>
        {members.length === 0 && <EmptyState>No students in this group yet.</EmptyState>}
      </div>

      <OverlayFooter className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => setPicked(allIn ? new Set() : new Set(members.map((m) => m.id)))}
          className="cursor-pointer border-none bg-transparent p-0 text-[13.5px] font-semibold whitespace-nowrap text-brand"
        >
          {allIn ? 'Clear' : 'All in'}
        </button>
        <PrimaryButton className="flex-1 p-[15px] text-[15px]" onClick={submit} disabled={!activity}>
          Save · {selected.size} of {members.length} in
        </PrimaryButton>
      </OverlayFooter>

      {search.step === 'pick' && (
        <ActivityPicker
          activities={activities}
          selectedId={activity?.id}
          onBack={back}
          onNew={() => void navigate({ to: '.', search: (s) => ({ ...s, step: 'new' }) })}
          onSelect={(a) => setStep(undefined, a.id)}
        />
      )}
      {search.step === 'new' && <NewActivityForm onBack={back} onCreated={(a) => setStep(undefined, a.id)} />}
    </Overlay>
  );
}
